// Standalone copy of the one helper the desk takes from the CRM's desk-data.ts: the polling hook.
// (The rest of that file is CRM-only: reminders, inbox, documents.) Kept identical to the original.
import { useCallback, useEffect, useRef, useState } from 'react';

export const REFRESH_MS = 15_000;

const messageOf = (cause: unknown, fallback: string): string => (cause instanceof Error && cause.message !== '' ? cause.message : fallback);

export type Live<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
  updatedAt: Date | null;
};

export const useLiveData = <T,>(load: () => Promise<T>, intervalMs: number = REFRESH_MS): Live<T> => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const alive = useRef(true);
  const loader = useRef(load);

  loader.current = load;

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const next = await loader.current();

      if (!alive.current) return;

      setData(next);
      setError(null);
      setUpdatedAt(new Date());
    } catch (cause) {
      if (alive.current) setError(messageOf(cause, 'Could not load from the server.'));
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void refresh();

    const timer = setInterval(() => void refresh(), intervalMs);
    const onFocus = (): void => {
      if (document.visibilityState !== 'hidden') void refresh();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      alive.current = false;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refresh, intervalMs]);

  return { data, error, loading: data === null && error === null, refresh, updatedAt };
};
